// Generic row-grouping helper for the shared DataTable. Generalizes the
// CofA-registry `groupDocuments()` shape (vendor/product/date) into a
// type-agnostic bucketer that any module can reuse — Sales Inbox, CofA
// Pipeline, Quotes, Regulatory, Support all group their pipeline rows by
// stage on top of this.
//
// The one capability the older CofA helper lacks: when `groupOrder` is
// supplied, EVERY listed key produces a group — including ones with zero
// rows — in the exact order given. That's what renders the empty
// "Completed (0)" stage headers operators expect from an Odoo-style
// grouped list.

export type GroupAccent = 'neutral' | 'success' | 'warning' | 'danger'

export interface TableGroup<T> {
  /** Stable bucket key (matches a `groupOrder` entry when one is given). */
  key: string
  /** Display label for the group header. */
  label: string
  /** Optional header accent — drives the dot/tint on the group row. */
  accent?: GroupAccent
  items: T[]
}

export interface GroupRowsOptions<T> {
  /** Bucket key for a row. Return '' / nullish → folded into the placeholder. */
  keyOf: (row: T) => string | null | undefined
  /**
   * Full ordered list of keys. When provided, groups render in this exact
   * order and keys with no rows still emit an (empty) group — this is the
   * stage-grouping path. When omitted, keys are derived from the data and
   * sorted alphabetically (the vendor/product/customer path).
   */
  groupOrder?: ReadonlyArray<string>
  /** Human label for a key. */
  labelOf: (key: string) => string
  /** Optional accent for a key. */
  accentOf?: (key: string) => GroupAccent | undefined
}

/** Sentinel key for rows whose `keyOf` is empty/nullish. Always sorts last. */
export const NO_GROUP_KEY = '__none__'

export function groupRows<T>(
  rows: readonly T[],
  opts: GroupRowsOptions<T>,
): TableGroup<T>[] {
  const { keyOf, groupOrder, labelOf, accentOf } = opts

  const buckets = new Map<string, T[]>()
  for (const row of rows) {
    const raw = keyOf(row)
    const key = raw && String(raw).trim() ? String(raw).trim() : NO_GROUP_KEY
    const list = buckets.get(key)
    if (list) list.push(row)
    else buckets.set(key, [row])
  }

  const make = (key: string): TableGroup<T> => ({
    key,
    label: labelOf(key),
    accent: accentOf?.(key),
    items: buckets.get(key) ?? [],
  })

  // Fixed-order path: emit every declared key (empty groups included), then
  // any stray keys present in the data but not in groupOrder (e.g. a legacy
  // stage), with the placeholder bucket last.
  if (groupOrder && groupOrder.length > 0) {
    const declared = new Set(groupOrder)
    const groups = groupOrder.map(make)
    const extras = Array.from(buckets.keys())
      .filter((k) => !declared.has(k) && k !== NO_GROUP_KEY)
      .sort((a, b) => labelOf(a).localeCompare(labelOf(b), undefined, { sensitivity: 'base' }))
    for (const k of extras) groups.push(make(k))
    if (buckets.has(NO_GROUP_KEY)) groups.push(make(NO_GROUP_KEY))
    return groups
  }

  // Derived path: only keys present in the data, alphabetical, placeholder last.
  const keys = Array.from(buckets.keys())
  keys.sort((a, b) => {
    if (a === NO_GROUP_KEY) return 1
    if (b === NO_GROUP_KEY) return -1
    return labelOf(a).localeCompare(labelOf(b), undefined, { sensitivity: 'base' })
  })
  return keys.map(make)
}
